import {
  Scene,
  ArcRotateCamera,
  HemisphericLight,
  Camera,
  Color3,
  Color4,
  Vector3,
} from "@babylonjs/core";
import type { App, Screen } from "../App";
import { LocalStore } from "../store/LocalStore";
import { joinQrDataUrl } from "../../multiplayer/shared/joinQr";

export class MainMenu implements Screen {
  private readonly scene: Scene;
  private readonly root: HTMLDivElement;

  constructor(private readonly app: App) {
    // Minimal Babylon scene (dark background)
    this.scene = new Scene(app.engine);
    this.scene.clearColor = new Color4(0.05, 0.05, 0.1, 1);
    const cam = new ArcRotateCamera("cam", 0, 0, 20, Vector3.Zero(), this.scene);
    cam.mode = Camera.ORTHOGRAPHIC_CAMERA;
    cam.orthoLeft = -10;
    cam.orthoRight = 10;
    cam.orthoTop = 10;
    cam.orthoBottom = -10;
    const amb = new HemisphericLight("amb", new Vector3(0, 1, 0), this.scene);
    amb.intensity = 0.3;
    amb.diffuse = new Color3(0.5, 0.6, 1);

    // DOM menu
    const store = new LocalStore();
    const campaignScores = store.getHighScores("campaign");
    const survivalScores = store.getHighScores("survival");
    const campaignRows = campaignScores.length
      ? campaignScores
          .slice(0, 5)
          .map((s, i) => `<tr><td>${i + 1}</td><td>${s.score.toLocaleString()}</td></tr>`)
          .join("")
      : '<tr><td colspan="2" style="color:#666">No scores yet</td></tr>';
    const survivalRows = survivalScores.length
      ? survivalScores
          .slice(0, 5)
          .map(
            (s, i) =>
              `<tr><td>${i + 1}</td><td>${s.score.toLocaleString()}</td><td>${s.waveReached ?? "-"}</td></tr>`,
          )
          .join("")
      : '<tr><td colspan="3" style="color:#666">No scores yet</td></tr>';
    const shareUrl = currentGameUrl();
    const qrUrl = joinQrDataUrl(shareUrl);

    this.root = document.createElement("div");
    this.root.style.cssText = `
      position:fixed;inset:0;display:flex;flex-direction:column;
      align-items:center;justify-content:center;
      padding:24px 16px; overflow:auto;
      color:#eee;font-family:monospace;text-align:center;z-index:200;
    `;
    this.root.innerHTML = `
      <div style="font-size:clamp(34px, 10vw, 48px);font-weight:bold;letter-spacing:4px;color:#3cf;text-shadow:0 0 20px #06f;margin-bottom:8px">
        3D TANKS
      </div>
      <div style="font-size:14px;color:#888;margin-bottom:16px">Twin-stick arena combat</div>
      <div style="margin-bottom:24px;display:flex;gap:12px;flex-wrap:wrap;justify-content:center">
        <a href="${appUrl("leaderboard.html")}" style="font-size:14px;color:#38bdf8;text-decoration:none;font-weight:bold;border:1px solid #38bdf8;padding:6px 12px;border-radius:4px;display:inline-block;font-family:monospace;">🏆 GLOBAL LEADERBOARD</a>
        <a href="${appUrl("")}" style="font-size:14px;color:#8cff3d;text-decoration:none;font-weight:bold;border:1px solid #8cff3d;padding:6px 12px;border-radius:4px;display:inline-block;font-family:monospace;">🎮 ALL GAME MODES</a>
      </div>

      <div style="display:flex;gap:16px;margin-bottom:32px;flex-wrap:wrap;justify-content:center">
        <button id="btn-tutorial" style="
          font-size:20px;font-family:monospace;padding:12px 30px;
          background:#8cff3d;color:#102000;border:none;border-radius:6px;cursor:pointer;
          box-shadow:0 0 20px rgba(140,255,61,.4);
        ">TUTORIAL</button>
        <button id="btn-campaign" style="
          font-size:20px;font-family:monospace;padding:12px 30px;
          background:#47d7ff;color:#001821;border:none;border-radius:6px;cursor:pointer;
          box-shadow:0 0 20px rgba(71,215,255,.5);
        ">CAMPAIGN</button>
        <button id="btn-survival" style="
          font-size:20px;font-family:monospace;padding:12px 30px;
          background:#ffd447;color:#211800;border:none;border-radius:6px;cursor:pointer;
          box-shadow:0 0 20px rgba(255,212,71,.5);
        ">SURVIVAL</button>
      </div>

      <div style="
        display:flex;align-items:center;gap:14px;justify-content:center;flex-wrap:wrap;
        margin-bottom:24px;padding:12px 14px;border:1px solid rgba(56,189,248,.35);
        background:rgba(11,18,32,.72);border-radius:8px;box-shadow:0 0 20px rgba(56,189,248,.12);
        max-width:min(460px, calc(100vw - 32px));
      ">
        <div style="background:#fff;padding:8px;border-radius:6px;line-height:0">
          <img src="${qrUrl}" alt="QR code to play 3D Tanks" width="156" height="156" style="display:block" />
        </div>
        <div style="text-align:left;min-width:180px;max-width:240px">
          <div style="font-size:13px;color:#8cff3d;font-weight:bold;margin-bottom:6px;letter-spacing:.04em">SCAN TO PLAY</div>
          <div style="font-size:12px;color:#aaa;line-height:1.45;margin-bottom:8px">Point a phone camera at this screen to open the game.</div>
          <div style="font-size:11px;color:#47d7ff;word-break:break-all;line-height:1.35">${escapeHtml(shareUrl)}</div>
        </div>
      </div>

      <div style="font-size:12px;color:#aaa;margin-bottom:24px;line-height:1.6;max-width:min(560px, calc(100vw - 32px))">
        WASD move &nbsp;|&nbsp; Mouse aim &nbsp;|&nbsp; LMB / Space fire &nbsp;|&nbsp; Shift dash
        <br>Touch drag to drive &nbsp;|&nbsp; Touch mode auto-fires at nearest enemy
        <br>Backtick — debug overlay &nbsp;|&nbsp; I — invincible &nbsp;|&nbsp; K — kill all
      </div>

      <div style="display:flex;gap:36px;align-items:flex-start;justify-content:center;flex-wrap:wrap">
        <div>
          <div style="font-size:13px;color:#47d7ff;margin-bottom:8px">CAMPAIGN SCORES</div>
          <table style="border-collapse:collapse;font-size:14px;color:#ccc;min-width:180px">
            <tbody>${campaignRows}</tbody>
          </table>
        </div>
        <div>
          <div style="font-size:13px;color:#ffd447;margin-bottom:8px">SURVIVAL SCORES</div>
          <table style="border-collapse:collapse;font-size:14px;color:#ccc;min-width:220px">
            <tbody>${survivalRows}</tbody>
          </table>
        </div>
      </div>
    `;
    document.body.appendChild(this.root);

    const tutorialBtn = this.root.querySelector<HTMLButtonElement>("#btn-tutorial");
    tutorialBtn?.addEventListener("click", () => this.startTutorial());
    const campaignBtn = this.root.querySelector<HTMLButtonElement>("#btn-campaign");
    campaignBtn?.addEventListener("click", () => this.startGame("campaign"));
    const survivalBtn = this.root.querySelector<HTMLButtonElement>("#btn-survival");
    survivalBtn?.addEventListener("click", () => this.startGame("survival"));

    // Any key also starts
    this.root.addEventListener("keydown", (e) => {
      if (e.code === "Enter" || e.code === "Space") this.startGame("campaign");
    });
    this.root.setAttribute("tabindex", "0");
    this.root.focus();
  }

  private startGame(mode: "campaign" | "survival"): void {
    void import("./GameWorld").then(({ GameWorld }) => {
      this.app.switchTo(new GameWorld(this.app, { mode }));
    });
  }

  private startTutorial(): void {
    void import("./GameWorld").then(({ GameWorld }) => {
      this.app.switchTo(new GameWorld(this.app, { mode: "campaign", tutorial: true }));
    });
  }

  render(): void {
    this.scene.render();
  }

  dispose(): void {
    this.root.remove();
    this.scene.dispose();
  }
}

function appUrl(path: string): string {
  return new URL(path, window.location.origin + baseUrl()).toString();
}

function currentGameUrl(): string {
  return new URL(window.location.pathname, window.location.origin).toString();
}

function baseUrl(): string {
  return import.meta.env.BASE_URL;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
